import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import Select from "react-select";
import _ from "lodash";
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

import InputCell from "@/common/form/InputCell";
import { TIMEZONES } from "@/utils/utils";

const TENANT_LABELS = [
    { key: 'engage',   title: 'Engage' },
    { key: 'ready',    title: 'Ready' },
    { key: 'pss',      title: 'PSS' },
    { key: 'churn',    title: 'Churn' },
    { key: 'internal', title: 'Internal' },
];

export default function EditTenant({ tenant, onSuccess, onCancel }) {
    const companyNameRef = React.createRef();
    const testEmailRef = React.createRef();
    const salesforceIdRef = React.createRef();
    const siteKeyInternalRef = React.createRef()
    const globalEmployerIdRef = React.createRef()
    const [timezoneObj, setTimezoneObj] = React.useState(tenant.timezoneName ? _.find(TIMEZONES, function (tz) { return tz.value === tenant.timezoneName }) : TIMEZONES[0]);
    const [labels, setLabels] = React.useState({
        engage:   !!tenant.engage,
        ready:    !!tenant.ready,
        pss:      !!tenant.pss,
        churn:    !!tenant.churn,
        internal: !!tenant.internal,
    });
    const toggleLabel = (key) => setLabels(prev => ({ ...prev, [key]: !prev[key] }));
    const hasLabel = Object.values(labels).some(Boolean);

    const submitEdit = () => {
        API.put('Core', '/api/v1/support-tools/tenants/' + tenant.id, {
            body: {
                name: companyNameRef.current.value,
                timezoneName: timezoneObj.value,
                testEmailInboxAddress: testEmailRef.current.value,
                salesforceId: salesforceIdRef.current.value,
                siteKeyInternal: siteKeyInternalRef.current.value === '' ? null: siteKeyInternalRef.current.value,
                globalEmployerId: globalEmployerIdRef.current.value === '' ? null: globalEmployerIdRef.current.value,
                ...labels,
            }
        })
            .then(response => {
                toast("Successfully saved tenant!", { position: "top-center", type: "success" });
                onSuccess();
            },
                error => {
                    toast("Could not save tenant: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            );
    }

    const onTimezoneChange = (obj, action) => {
        setTimezoneObj(obj);
    }

    return (
        <div>
            <div className="content">
                <div className="tableView">
                    <div className="tableSection sectionName">
                        <InputCell
                            placeholder={"Tenant Name"}
                            defaultValue={tenant.name}
                            required
                            id="tenantName"
                            label="Tenant Name"
                            inputRef={companyNameRef}
                        />
                    </div>
                    <div className="tableSection sectionHandoffType">
                        <div className="sectionTitle">Timezone</div>
                        <Select className="cell selectCell" classNamePrefix="selectCell"
                                onChange={onTimezoneChange}
                                defaultValue={timezoneObj}
                                value={timezoneObj}
                                options={TIMEZONES}
                        />

                    </div>

                    <div className="tableSection sectionName">
                        <InputCell
                            placeholder={"Test Email Inbox"}
                            defaultValue={tenant.testEmailInboxAddress}
                            required
                            label="Test Email Inbox"
                            inputRef={testEmailRef}
                        />
                    </div>

                    <div className="tableSection sectionName">
                        <InputCell
                            placeholder={"Salesforce Id"}
                            defaultValue={tenant.salesforceId}
                            label="Salesforce Id"
                            inputRef={salesforceIdRef}
                        />
                    </div>
                    <div className="tableSection sectionName">
                        <div className="sectionTitle">Foundation/PowerSSO</div>
                        <InputCell
                            defaultValue={tenant.siteKeyInternal}
                            id="siteKeyInternal"
                            label="Site Key Internal"
                            inputRef={siteKeyInternalRef}
                        />
                        <InputCell
                            defaultValue={tenant.globalEmployerId}
                            id="globalEmployerId"
                            label="Global Employer ID"
                            inputRef={globalEmployerIdRef}
                        />
                    </div>

                    <div className="tableSection">
                        <div className="sectionTitle" id="tenantTypeLabelEdit">
                            Tenant Type <span aria-hidden="true" style={{ color: 'var(--colorRequiredText)' }}>*</span>
                        </div>
                        <span id="tenantTypeLabelEditRequired" style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>required</span>
                        <FormGroup row role="group" aria-labelledby={`tenantTypeLabelEdit tenantTypeLabelEditRequired${!hasLabel ? ' tenantTypeLabelEditError' : ''}`}>
                            {TENANT_LABELS.map(({ key, title }) => (
                                <FormControlLabel
                                    key={key}
                                    label={title}
                                    control={
                                        <Checkbox
                                            checked={labels[key]}
                                            onChange={() => toggleLabel(key)}
                                            inputProps={{ 'aria-label': title }}
                                        />
                                    }
                                />
                            ))}
                        </FormGroup>
                        <div id="tenantTypeLabelEditError" className="cellFootnote" style={{ color: 'var(--colorRequiredText)' }} aria-live="polite" aria-atomic="true">
                            {!hasLabel ? 'Please select at least one tenant type.' : ''}
                        </div>
                    </div>
                </div>
            </div>
            <div className="actionBar">
                <div className="actions">
                    <div
                        onClick={onCancel}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCancel(); } }}
                        className="button dismiss medium"
                        role="button"
                        tabIndex={0}
                        aria-label="Cancel"
                    >
                        <div className="title">Cancel</div>
                    </div>

                    <div
                        onClick={hasLabel ? submitEdit : undefined}
                        onKeyDown={hasLabel ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); submitEdit(); } }) : undefined}
                        className={`button confirm medium${!hasLabel ? ' disabled' : ''}`}
                        role="button"
                        tabIndex={hasLabel ? 0 : -1}
                        aria-label="Save"
                        aria-disabled={!hasLabel}
                    >
                        <div className="title">Save</div>
                    </div>
                </div>
                <div className="separator"></div>
            </div>
        </div>
    );
}